import { LoadingButton } from "@mui/lab";
import { Alert, AlertTitle, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import { MdsIcon } from "@sinch/components";
import { FormContainer } from "@sinch/components/form/FormContainer/FormContainer";
import { useGlobalApi } from "@sinch/core/hooks/useGlobalApi";
import type { useMuiForm } from "@sinch/hooks/useMuiForm";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { MdsError } from "@sinch/icons";
import { useSnackbar } from "notistack";
import { isEmpty } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { FC, type ReactNode, useCallback, useMemo, useState } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MessageFormFields } from "./formSchema";
import { RecipientsInput } from "./RecipientsInput";
import { SMSContent } from "./SMSContent";
import { Contactlist } from "@sinch/views/ClientReminderDialog/queries.graphql";
import { ContactlistQuery, ContactlistQueryVariables } from "@sinch/views/ClientReminderDialog/queries.types";
import { useClientReminderDialog } from "@sinch/views/ClientReminderDialog/useClientReminderDialog";

interface OpenClientReminderDialogProps {
  shiftId: string | number;
}

export type Placeholders = { label: ReactNode; value: string; size: number }[];

/**
 * Component to render a message dialog for sending messages.
 */
export const ClientReminderDialog: FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dialogData, setDialogData] = useState<OpenClientReminderDialogProps | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const clientReminderApiMethods = useMemo(
    () => ({
      openDialog: (props: OpenClientReminderDialogProps) => {
        setDialogData(props);
        setOpen(true);
      },
    }),
    []
  );
  useGlobalApi("ClientReminderDialog", clientReminderApiMethods);

  /**
   * Callback fired on successful message sending.
   */
  const onSuccess = useCallback(() => {
    enqueueSnackbar(t("Messages.sent"), { variant: "success" });
    setOpen(false);
  }, [enqueueSnackbar, t]);

  /**
   * Callback fired when closing the dialog.
   */
  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const { form, handleSubmit } = useClientReminderDialog({
    shiftId: (dialogData as OpenClientReminderDialogProps)?.shiftId,
    onSuccess: () => {
      onSuccess();
    },
  });

  if (!dialogData?.shiftId) {
    return;
  }

  return (
    <Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
      <FormContainer form={form}>
        <DialogTitle>{t("MessageDialog.clientReminderTitle")}</DialogTitle>
        <DialogContent>
          <SendMessageForm shiftId={dialogData.shiftId} />
        </DialogContent>
        <DialogActions>
          <MessageDialogActionButtons onClose={onClose} onSubmit={handleSubmit} />
        </DialogActions>
      </FormContainer>
    </Dialog>
  );
};

const MessageDialogActionButtons = ({ onClose, onSubmit }: { onClose: () => void; onSubmit: () => void }) => {
  const { t } = useTranslation();
  const { isValid, isSubmitting } = useFormState<MessageFormFields>();

  return (
    <>
      <Button onClick={onClose}>{t("cancel")}</Button>
      <LoadingButton disabled={!isValid} loading={isSubmitting} onClick={onSubmit} variant="contained">
        {t("Messages.send")}
      </LoadingButton>
    </>
  );
};

/**
 * Component to render the form for sending messages.
 */
const SendMessageForm = ({ shiftId }: { shiftId: string | number }) => {
  const { t } = useTranslation();
  const { getFormErrors } = useFormContext() as ReturnType<typeof useMuiForm>;

  const formErrors = getFormErrors() as string[];

  const { data } = useQueryHandler<ContactlistQuery, ContactlistQueryVariables>(Contactlist, {
    shiftId: String(shiftId),
  });
  const recipients =
    (data?.shiftContactlists
      ?.map((contact) => ({
        name: contact.name ?? "",
        phone: contact.phone,
      }))
      .filter((contact) => isNotNil(contact.phone)) as { name: string; phone: string }[]) ?? [];

  return (
    <>
      <Stack spacing={3}>
        <RecipientsInput recipients={recipients} />

        <SMSContent recipients={recipients} />

        {!isEmpty(formErrors) && (
          <Alert icon={<MdsIcon icon={MdsError} />} severity="error" sx={{ mb: 2 }}>
            <Stack>
              <AlertTitle sx={{ width: "100%" }}>{t("Error.somethingWentWrong")}</AlertTitle>
              {formErrors.map((message) => (
                <Typography key={message}>{message}</Typography>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    </>
  );
};
